/**
 * Medlose dataLayer Tracking
 * Tracks quiz funnels, form submissions, link clicks, and button clicks.
 * SPA-proof via history.pushState interception.
 *
 * Also handles marketing attribution: generates lead_id, captures gclid and
 * computes bron/medium, injects them into the Shopify form hidden fields, and
 * passes them along on the conversion dataLayer events for Enhanced Conversions.
 *
 * Meta Pixel (base code in layout/theme.liquid): fires `Lead` on the quiz
 * submit and `Contact` on the contact forms, alongside the dataLayer events.
 */
(function () {
  'use strict';

  window.dataLayer = window.dataLayer || [];

  // ── Helpers ──────────────────────────────────────────────

  async function sha256Hash(value) {
    if (!value || !value.trim()) return '';
    var data = new TextEncoder().encode(value.trim().toLowerCase());
    var hash = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(hash))
      .map(function (b) { return b.toString(16).padStart(2, '0'); })
      .join('');
  }

  function getPagePath() {
    return window.location.pathname + window.location.search;
  }

  function getStepFromURL() {
    var params = new URLSearchParams(window.location.search);
    var step = parseInt(params.get('step'), 10);
    return isNaN(step) ? 0 : step;
  }

  /**
   * Detect which quiz flow we're on based on URL path.
   * Returns null if not on a quiz page.
   */
  function getFlowType() {
    var path = window.location.pathname;
    if (path.indexOf('questions-flow-details') !== -1) return 'details';
    if (path.indexOf('question-flow') !== -1 || path.indexOf('questions-flow') !== -1) return 'short';
    return null;
  }

  function getEventName(flowType) {
    return flowType === 'details' ? 'vragenlijst_details_step' : 'vragenlijst_step';
  }

  function getFormName(flowType) {
    return flowType === 'details' ? 'vragenlijst_form_details_sent' : 'vragenlijst_form_sent';
  }

  function getFormId(flowType) {
    return flowType === 'details' ? '2' : '1';
  }

  // Meta Pixel standard event. No-op when the pixel isn't loaded (theme editor,
  // blocked by the browser). lead_id is passed as eventID so a later Conversions
  // API event for the same lead can be deduplicated against this one.
  function trackMeta(eventName, params, eventId) {
    if (typeof window.fbq !== 'function') return;
    try {
      window.fbq('track', eventName, params || {}, eventId ? { eventID: eventId } : {});
      console.log('[Medlose Tracking] meta', eventName, params);
    } catch (e) { /* never break the form flow */ }
  }

  // ── Marketing Attribution (lead_id / gclid / bron-medium) ─

  function safeGet(storage, key) {
    try { return storage.getItem(key); } catch (e) { return null; }
  }
  function safeSet(storage, key, value) {
    try { storage.setItem(key, value); } catch (e) { /* storage blocked */ }
  }

  function generateUUID() {
    if (window.crypto && crypto.randomUUID) {
      try { return crypto.randomUUID(); } catch (e) { /* fall through */ }
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      var v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  // Unique id per session/form-entry. Persisted for the browser session.
  function getLeadId() {
    var key = 'md_lead_id';
    var val = safeGet(sessionStorage, key);
    if (!val) {
      val = generateUUID();
      safeSet(sessionStorage, key, val);
    }
    return val;
  }

  // Google Click ID from the URL, persisted (survives multi-step navigation).
  function getGclid() {
    var key = 'md_gclid';
    var params = new URLSearchParams(window.location.search);
    var fromUrl = params.get('gclid');
    if (fromUrl) {
      safeSet(localStorage, key, fromUrl);
      return fromUrl;
    }
    return safeGet(localStorage, key) || '';
  }

  var SEARCH_ENGINES = [
    { name: 'google', match: 'google.' },
    { name: 'bing', match: 'bing.' },
    { name: 'duckduckgo', match: 'duckduckgo.' },
    { name: 'yahoo', match: 'yahoo.' },
    { name: 'ecosia', match: 'ecosia.' },
    { name: 'startpage', match: 'startpage.' }
  ];

  // Format: "bron / medium" following the briefing's priority order.
  function computeBronMedium() {
    var params = new URLSearchParams(window.location.search);
    var src = params.get('utm_source');
    var med = params.get('utm_medium');

    // 1. UTM parameters present
    if (src && med) return src + ' / ' + med;

    // 2. No UTM, but a Google Ads click
    if (params.get('gclid')) return 'google / cpc';

    var ref = document.referrer;
    if (ref) {
      try {
        var host = new URL(ref).hostname;
        if (host && host !== window.location.hostname) {
          // 3. Search referrer => organic
          for (var i = 0; i < SEARCH_ENGINES.length; i++) {
            if (host.indexOf(SEARCH_ENGINES[i].match) !== -1) {
              return SEARCH_ENGINES[i].name + ' / organic';
            }
          }
          // 4. Other external referrer => referral
          return host.replace(/^www\./, '') + ' / referral';
        }
      } catch (e) { /* invalid referrer URL */ }
    }

    // 5. No params, empty referrer => direct
    return 'direct / none';
  }

  // First-touch within the session: only computed once, then persisted.
  function getBronMedium() {
    var key = 'md_bronmedium';
    var existing = safeGet(sessionStorage, key);
    if (existing) return existing;
    var val = computeBronMedium();
    safeSet(sessionStorage, key, val);
    return val;
  }

  function getMarketingData() {
    return {
      lead_id: getLeadId(),
      gclid: getGclid(),
      bronmedium_marketing: getBronMedium()
    };
  }

  // Inject the marketing values into the hidden form fields so they are
  // submitted to Shopify together with the contact/customer record.
  function fillHiddenFields(scope) {
    var root = scope || document;
    var data = getMarketingData();
    var map = {
      '.js-md-lead-id': data.lead_id,
      '.js-md-gclid': data.gclid,
      '.js-md-bronmedium': data.bronmedium_marketing
    };
    Object.keys(map).forEach(function (sel) {
      var els = root.querySelectorAll(sel);
      for (var i = 0; i < els.length; i++) els[i].value = map[sel] || '';
    });
  }

  // ── 1. Quiz Step Tracking ────────────────────────────────

  var _lastStep = null;
  var _lastClickText = null;

  function pushQuizStep() {
    var flowType = getFlowType();
    if (!flowType) return;

    var step = getStepFromURL();
    if (step === _lastStep) return; // deduplicate
    _lastStep = step;

    var payload = {
      event: getEventName(flowType),
      step_number: step,
      page_path: getPagePath(),
      form_url: step === 0 ? window.location.href : null,
      click_text: _lastClickText
    };

    window.dataLayer.push(payload);
    console.log('[Medlose Tracking]', payload.event, payload);
    _lastClickText = null;
  }

  // Capture click text from the next button before pushState fires
  function setupClickTextCapture() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-next-button], [data-prev-button]');
      if (btn) {
        _lastClickText = btn.textContent.trim() || null;
      }
    }, true); // capture phase, fires before pushState
  }

  // Monkey-patch history.pushState to detect SPA navigation
  function patchPushState() {
    var originalPushState = history.pushState;
    history.pushState = function () {
      originalPushState.apply(this, arguments);
      // Small delay to ensure URL is updated
      setTimeout(pushQuizStep, 50);
    };

    // Also listen for popstate (back/forward browser buttons)
    window.addEventListener('popstate', function () {
      setTimeout(pushQuizStep, 50);
    });
  }

  // ── 2. Quiz Form Submission ──────────────────────────────

  var _quizSubmitted = false;

  function patchFetch() {
    var originalFetch = window.fetch;
    window.fetch = function () {
      var url = arguments[0];
      var options = arguments[1];

      // Detect webhook POST to n8n
      if (
        typeof url === 'string' &&
        url.indexOf('att.opklopper.site') !== -1 &&
        options &&
        options.method === 'POST' &&
        !_quizSubmitted
      ) {
        _quizSubmitted = true;
        try {
          var body = JSON.parse(options.body);
          var flowType = getFlowType();
          var email = body.email || '';
          var phone = '';

          // Phone lives in a weight-input (data-question-input) on the details
          // flow and could be a contact-input elsewhere — match both.
          var phoneInput = document.querySelector(
            '[data-contact-input][type="tel"], [data-question-input][type="tel"]'
          );
          if (phoneInput) phone = phoneInput.value.trim();

          var marketing = getMarketingData();

          trackMeta('Lead', { content_name: getFormName(flowType) }, marketing.lead_id);

          // Hash and push asynchronously
          Promise.all([sha256Hash(email), sha256Hash(phone)]).then(function (hashes) {
            var payload = {
              event: getFormName(flowType),
              form_id: getFormId(flowType),
              form_name: getFormName(flowType),
              // Raw values for Google Enhanced Conversions (auto-hashed by Google)
              email: email,
              phone_number: phone,
              // Pre-hashed values
              form_email: hashes[0],
              form_phone: hashes[1],
              // Marketing attribution
              lead_id: marketing.lead_id,
              gclid: marketing.gclid,
              bronmedium_marketing: marketing.bronmedium_marketing,
              page_path: getPagePath()
            };
            window.dataLayer.push(payload);
            console.log('[Medlose Tracking]', payload.event, payload);
          });
        } catch (e) {
          console.error('[Medlose Tracking] Error parsing form data:', e);
        }
      }

      return originalFetch.apply(this, arguments);
    };
  }

  // ── 3. Contact Form Tracking ─────────────────────────────

  // Forms that have already pushed their dataLayer event and may submit freely.
  var _handledContactForms = new WeakSet();

  function setupContactFormTracking() {
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || !form.id || form.id.indexOf('ContactForm-') !== 0) return;

      // Already handled once → let the native submit proceed (fires only once).
      if (_handledContactForms.has(form)) return;

      e.preventDefault();
      _handledContactForms.add(form);

      // Make sure the marketing hidden fields are populated before submitting.
      fillHiddenFields(form);

      var emailInput = form.querySelector('input[type="email"]');
      var phoneInput = form.querySelector('input[type="tel"]');
      var email = emailInput ? emailInput.value.trim() : '';
      var phone = phoneInput ? phoneInput.value.trim() : '';
      var marketing = getMarketingData();

      trackMeta('Contact', { content_name: 'contactform_sent' }, marketing.lead_id);

      function submitForm() {
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit(); // re-dispatches submit; guarded by WeakSet above
        } else {
          form.submit();
        }
      }

      Promise.all([sha256Hash(email), sha256Hash(phone)]).then(function (hashes) {
        var payload = {
          event: 'contactform_sent',
          form_id: '3',
          form_name: 'contactform_sent',
          email: email,
          phone_number: phone,
          form_email: hashes[0],
          form_phone: hashes[1],
          lead_id: marketing.lead_id,
          gclid: marketing.gclid,
          bronmedium_marketing: marketing.bronmedium_marketing,
          page_path: getPagePath()
        };
        window.dataLayer.push(payload);
        console.log('[Medlose Tracking]', payload.event, payload);

        // Give GTM a brief moment to process the event, then submit.
        setTimeout(submitForm, 200);
      }).catch(function () {
        submitForm();
      });
    }, true);
  }

  // ── 4. Link Click Tracking ───────────────────────────────

  function setupLinkTracking() {
    document.addEventListener('click', function (e) {
      var link = e.target.closest('a[href]');
      if (!link) return;

      var href = link.getAttribute('href') || '';
      var text = link.textContent.trim();

      // Email click
      if (href.indexOf('mailto:') === 0) {
        window.dataLayer.push({
          event: 'email_click',
          click_text: text,
          click_url: href
        });
        console.log('[Medlose Tracking] email_click', href);
        return;
      }

      // Phone click
      if (href.indexOf('tel:') === 0) {
        window.dataLayer.push({
          event: 'phone_click',
          click_text: text,
          click_url: href
        });
        console.log('[Medlose Tracking] phone_click', href);
        return;
      }

      // Outbound click
      try {
        if (href.indexOf('http') === 0) {
          var linkHost = new URL(href).hostname;
          if (linkHost !== window.location.hostname) {
            window.dataLayer.push({
              event: 'outbound_click',
              click_text: text,
              click_url: href
            });
            console.log('[Medlose Tracking] outbound_click', href);
          }
        }
      } catch (e) {
        // Invalid URL, skip
      }
    });
  }

  // ── 5. Button Click Tracking ─────────────────────────────

  function setupButtonTracking() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('button, a.btn, a.opk-main-btn');
      if (!btn) return;

      // Skip quiz navigation buttons (tracked as steps)
      if (btn.hasAttribute('data-next-button') || btn.hasAttribute('data-prev-button')) return;

      // Skip submit buttons on contact forms (tracked as form submissions)
      if (btn.type === 'submit' && btn.closest('form[id^="ContactForm-"]')) return;

      // Skip checkout buttons
      if (btn.name === 'checkout') return;

      // Skip links already tracked as email/phone/outbound
      if (btn.tagName === 'A') {
        var href = btn.getAttribute('href') || '';
        if (href.indexOf('mailto:') === 0 || href.indexOf('tel:') === 0) return;
        try {
          if (href.indexOf('http') === 0 && new URL(href).hostname !== window.location.hostname) return;
        } catch (e) { /* skip */ }
      }

      var payload = {
        event: 'button_click',
        click_text: btn.textContent.trim().substring(0, 100),
        click_url: btn.getAttribute('href') || btn.closest('form')?.action || null
      };

      window.dataLayer.push(payload);
      console.log('[Medlose Tracking] button_click', payload);
    });
  }

  // ── 6. Bedankt Page Tracking ─────────────────────────────

  function trackBedanktPage() {
    if (window.location.pathname.indexOf('/pages/bedankt') === -1) return;

    try {
      var quizData = JSON.parse(localStorage.getItem('quiz_user_data'));
      if (!quizData || !quizData.email) return;

      var marketing = getMarketingData();
      sha256Hash(quizData.email).then(function (hashedEmail) {
        var payload = {
          event: 'form_submit',
          form_id: '4',
          form_name: 'bedankt_page',
          email: quizData.email,
          form_email: hashedEmail,
          form_phone: '',
          lead_id: marketing.lead_id,
          gclid: marketing.gclid,
          bronmedium_marketing: marketing.bronmedium_marketing,
          page_path: getPagePath()
        };
        window.dataLayer.push(payload);
        console.log('[Medlose Tracking] bedankt page', payload);
      });
    } catch (e) {
      // No quiz data available
    }
  }

  // ── Init ─────────────────────────────────────────────────

  function init() {
    // Lock in marketing attribution as early as possible (capture gclid/referrer
    // before any SPA navigation rewrites the URL or referrer is lost).
    getMarketingData();

    // Patch APIs before anything else
    patchPushState();
    patchFetch();

    // Setup event listeners
    setupClickTextCapture();
    setupContactFormTracking();
    setupLinkTracking();
    setupButtonTracking();

    // Populate hidden marketing fields on any forms already in the DOM
    fillHiddenFields(document);

    // Fire initial quiz step if on a quiz page
    pushQuizStep();

    // Track bedankt page
    trackBedanktPage();

    console.log('[Medlose Tracking] Initialized');
  }

  // Run immediately (script is loaded after GTM)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
